import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Languages, Menu, MessageCircle, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C, COMPANY } from "@/data/content";
import { scrollToId } from "@/utils/scroll";
import Logo from "@/components/Logo";

const LINKS = [
    ["home", C.nav.home],
    ["about", C.nav.about],
    ["services", C.nav.services],
    ["compare", C.nav.compare],
    ["process", C.nav.process],
    ["markets", C.nav.markets],
    ["why-us", C.nav.whyus],
    ["contact", C.nav.contact],
];

export default function Navbar() {
    const { L, lang, setLang } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    const waHref = `${COMPANY.waHref}?text=${encodeURIComponent(
        L(C.contact.waPrefill)
    )}`;

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled || open
                    ? "glass border-b border-gold-line/60 py-2"
                    : "py-4"
            }`}
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6">
                <Logo />

                <ul className="hidden items-center gap-7 lg:flex">
                    {LINKS.map(([id, label]) => (
                        <li key={id}>
                            <button
                                data-testid={`nav-link-${id}`}
                                onClick={() => go(id)}
                                className="text-sm text-txt-2 transition-colors hover:text-brass"
                            >
                                {L(label)}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="language-toggle-button"
                        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
                        className="flex items-center gap-2 rounded-full border border-gold-line-strong px-4 py-2 text-xs font-semibold text-brass transition-colors hover:bg-brass hover:text-ink"
                    >
                        <Languages className="size-4" />
                        {lang === "ar" ? "EN" : "عربي"}
                    </button>

                    <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-whatsapp-cta"
                        className="hidden items-center gap-2 rounded-full bg-brass px-5 py-2.5 text-xs font-bold text-ink transition-transform hover:scale-105 sm:flex"
                    >
                        <MessageCircle className="size-4" />
                        {L(C.nav.cta)}
                    </a>

                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        className="grid size-10 place-items-center rounded-full border border-gold-line text-brass lg:hidden"
                        aria-label="Menu"
                    >
                        {open ? (
                            <X className="size-5" />
                        ) : (
                            <Menu className="size-5" />
                        )}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 top-0 -z-10 flex flex-col items-center justify-center gap-2 bg-ink/95 backdrop-blur-xl lg:hidden"
                    >
                        {LINKS.map(([id, label], i) => (
                            <motion.button
                                key={id}
                                data-testid={`mobile-nav-link-${id}`}
                                onClick={() => go(id)}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.06 * i, duration: 0.4 }}
                                className="font-display py-2 text-3xl text-txt-1 transition-colors hover:text-brass"
                            >
                                {L(label)}
                            </motion.button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
